/**
 * PortfolioImageService
 *
 * Uploads one portfolio image to the chatbot app, which reads the positions
 * out of it. The image is sent as raw multipart bytes — the server does the
 * base64 encoding the vision model needs.
 *
 * Does not use the shared requestJson helper: that sets a JSON content type,
 * while multipart needs the browser to set its own boundary header.
 */

import { chatbotBase } from '../config/api';
import { ApiError } from './api/client';

export interface PortfolioPosition {
  symbol: string;
  trade_date: string | null;
  cost_basis: number | null;
  quantity: number;
  side: string;
}

export interface PortfolioSnapshot {
  positions: PortfolioPosition[];
}

/** Server rejects anything larger; checked here too so the user hears it sooner. */
export const MAX_PORTFOLIO_IMAGE_BYTES = 10 * 1024 * 1024;

const API_BASE_URL = chatbotBase.replace(/\/$/, '');

const errorMessage = (payload: string, status: number): string => {
  const fallback = `Request failed with status ${status}`;
  if (!payload) return fallback;
  try {
    const parsed = JSON.parse(payload) as { detail?: unknown };
    const detail = parsed.detail;
    if (typeof detail === 'string') return detail;
    const message = (detail as { error?: { message?: string } } | undefined)?.error?.message;
    return message ?? fallback;
  } catch {
    return payload;
  }
};

export const extractPortfolioFromImage = async (
  file: File,
  signal?: AbortSignal
): Promise<PortfolioSnapshot> => {
  const body = new FormData();
  body.append('file', file);

  const response = await fetch(`${API_BASE_URL}/api/portfolio/image`, {
    method: 'POST',
    body,
    signal,
  });

  const payloadText = await response.text();
  if (!response.ok) {
    throw new ApiError(errorMessage(payloadText, response.status), response.status);
  }

  return JSON.parse(payloadText) as PortfolioSnapshot;
};
